import Route from '@ember/routing/route';
import { ajax } from 'discourse/lib/ajax';

export default class AdminPluginsProxyviewsDirectoryRoute extends Route {
  model() {
    // Promise.allSettled (not Promise.all) so one failing endpoint — e.g. the
    // backup summary 500ing because a sibling plugin is disabled — doesn't
    // take down the entire admin dashboard. Each section degrades to an empty
    // default if its fetch rejected; the dashboard's primary content (the
    // curation table) still loads as long as the dashboard endpoint itself
    // resolves.
    const fetches = [
      ajax('/admin/plugins/proxyviews-directory/dashboard'),
      ajax('/admin/plugins/proxyviews-directory/claims'),
      ajax('/admin/plugins/proxyviews-directory/calculator/pricing'),
      ajax('/admin/plugins/proxyviews-directory/calculator/regions'),
      ajax('/admin/plugins/proxyviews-directory/calculator/settings'),
      ajax('/admin/plugins/proxyviews-directory/calculator/provider-pricing'),
      // Full, unpaginated provider list — used for the provider picker in
      // the "New provider pricing override" form. The curation table's
      // `providers` is a 50-at-a-time paginated slice and isn't suitable
      // for that.
      ajax('/admin/plugins/proxyviews-directory/networks'),
      ajax('/admin/plugins/proxyviews-directory/overview'),
      ajax('/admin/plugins/proxyviews-directory/backup/summary'),
      ajax('/admin/plugins/proxyviews-directory/site-settings'),
      ajax('/admin/plugins/proxyviews-directory/featured-purchases'),
      ajax('/admin/plugins/proxyviews-directory/edit-proposals'),
    ];

    return Promise.allSettled(fetches).then((results) => {
      const val = (idx, fallback) => {
        const r = results[idx];
        return r && r.status === 'fulfilled' ? r.value : fallback;
      };
      const dashboard = val(0, {});
      const claims = val(1, { claims: [] });
      const pricingTiers = val(2, []);
      const regions = val(3, []);
      const settings = val(4, []);
      const providerPricings = val(5, []);
      const allProvidersRaw = val(6, { providers: [] });
      const overview = val(7, { overview: {} });
      const backup = val(8, { summary: {} });
      const siteSettings = val(9, []);
      const featuredRaw = val(10, { featured_purchases: [] });
      const editProposalsRaw = val(11, { edit_proposals: [] });

      return {
        providers: dashboard.providers || [],
        providersMeta: dashboard.meta || {},
        stats: dashboard.stats || {},
        claims: claims.claims || [],
        pricingTiers: pricingTiers || [],
        regions: regions || [],
        settings: settings || [],
        providerPricings: providerPricings || [],
        // Defensively unwrap in case the serializer roots each entry under
        // a "provider" key (as some of this plugin's other endpoints do).
        allProviders: (allProvidersRaw.providers || [])
          .map((raw) => raw.provider || raw)
          .map((p) => ({ id: p.id, name: p.name, slug: p.slug })),
        overview: overview.overview || {},
        backupSummary: backup.summary || {},
        siteSettings: siteSettings || [],
        featuredPurchases: featuredRaw.featured_purchases || featuredRaw.purchases || [],
        featuredPurchasesMeta: featuredRaw.meta || {},
        editProposals: editProposalsRaw.edit_proposals || editProposalsRaw.proposals || [],
      };
    });
  }

  setupController(controller, model) {
    super.setupController(controller, model);
    controller.providers = model.providers || [];
    controller.providersMeta = model.providersMeta || {};
    controller.providersPage = model.providersMeta?.page || 1;
    controller.stats = model.stats || {};
    controller.claims = model.claims || [];
    controller.pricingTiers = model.pricingTiers || [];
    controller.regions = model.regions || [];
    controller.settings = model.settings || [];
    controller.providerPricings = model.providerPricings || [];
    controller.allProviders = model.allProviders || [];
    controller.overview = model.overview || {};
    controller.backupSummary = model.backupSummary || {};
    controller.siteSettings = model.siteSettings || [];
    controller.featuredPurchases = model.featuredPurchases || [];
    controller.featuredPurchasesMeta = model.featuredPurchasesMeta || {};
    controller.editProposals = model.editProposals || [];
  }
}
